import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { pollPaymentStatus } from "@/hooks/useSubscription";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | ok | failed
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    (async () => {
      const r = await pollPaymentStatus(sessionId, { onUpdate: setDetail });
      setState(r.ok ? "ok" : "failed");
    })();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-void flex items-center justify-center p-6">
      <div className="max-w-md w-full brutal-border bg-[#141414] p-10 space-y-6 text-center" data-testid="payment-success">
        {state === "polling" && (
          <>
            <Loader2 className="mx-auto animate-spin text-signal-yellow" size={40} />
            <div className="label-eyebrow">// verifying payment</div>
            <p className="font-mono text-sm text-zinc-300">Hang tight. Confirming with Stripe...</p>
            {detail && <p className="font-mono text-[10px] text-zinc-600">status: {detail.payment_status}</p>}
          </>
        )}
        {state === "ok" && (
          <>
            <CheckCircle2 className="mx-auto text-signal-yellow" size={48} />
            <h1 className="heading-display text-3xl">WELCOME TO <span className="text-signal-yellow">PRO</span>.</h1>
            <p className="font-mono text-xs text-zinc-400">Unlimited generations unlocked. Let&apos;s go break the algorithm.</p>
            <Link
              to="/app"
              data-testid="success-return"
              className="inline-block bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-6 py-3 hover:bg-signal-yellow-hover transition-colors"
            >
              Enter the engine →
            </Link>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="mx-auto text-signal-red" size={40} />
            <h1 className="heading-display text-2xl">SOMETHING WENT SIDEWAYS.</h1>
            <p className="font-mono text-xs text-zinc-400">We couldn&apos;t confirm your payment. If you were charged, it will still activate — just refresh in a minute.</p>
            <Link to="/app" data-testid="success-return-fail" className="inline-block border border-white/20 hover:border-white/50 text-white font-mono uppercase tracking-[0.18em] text-xs px-6 py-3 transition-colors">
              Back to app →
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-void flex items-center justify-center p-6">
      <div className="max-w-md w-full brutal-border bg-[#141414] p-10 space-y-6 text-center" data-testid="payment-cancel">
        <div className="label-eyebrow">// checkout cancelled</div>
        <h1 className="heading-display text-3xl">NO WORRIES.</h1>
        <p className="font-mono text-xs text-zinc-400">You didn&apos;t upgrade. Free plan is still active. Come back when you&apos;re ready.</p>
        <div className="flex gap-2 justify-center">
          <Link to="/app/pricing" data-testid="cancel-retry" className="bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-4 py-3 hover:bg-signal-yellow-hover transition-colors">
            Try again →
          </Link>
          <Link to="/app" data-testid="cancel-back" className="border border-white/20 hover:border-white/50 text-white font-mono uppercase tracking-[0.18em] text-xs px-4 py-3 transition-colors">
            Back to app
          </Link>
        </div>
      </div>
    </div>
  );
}
